import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Truck, Plus, Pencil, Trash2, Wrench, AlertTriangle, Gauge, ClipboardCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../components/ui/sheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEPARTMENTS = ['service', 'new_construction', 'office'];
const MAINT_TYPES = ['Oil Change', 'Repair', 'Tires', 'Brakes', 'Battery', 'Other'];
const emptyForm = () => ({ name: '', vin: '', license_plate: '', year: '', make: '', model: '', department: 'service', assigned_to: '', insurance_expires: '', last_oil_change: '', next_oil_change: '', current_mileage: '', notes: '', active: true });
const todayISO = () => new Date().toISOString().slice(0, 10);
const emptyMaint = () => ({ date: todayISO(), mileage: '', type: 'Oil Change', notes: '', cost: '' });
const fmtMi = (n) => (n === null || n === undefined || n === '') ? '—' : `${Number(n).toLocaleString()} mi`;

const CHECK_ITEMS = [
  ['engine_oil', 'Engine oil'], ['power_steering_fluid', 'Power steering fluid'],
  ['washer_fluid', 'Washer fluid'], ['brake_fluid', 'Brake fluid'],
  ['brake_lights', 'Brake lights'], ['turn_signals', 'Turn signals'], ['headlights', 'Headlights'],
];

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  // Detail sheet (Maintenance & Repairs + weekly-check history)
  const [detail, setDetail] = useState(null);        // selected vehicle object
  const [maint, setMaint] = useState([]);
  const [checks, setChecks] = useState([]);
  const [maintForm, setMaintForm] = useState(emptyMaint());
  const [savingMaint, setSavingMaint] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [v, e] = await Promise.all([
        axios.get(`${API}/admin/vehicles`, { withCredentials: true }),
        axios.get(`${API}/admin/employees`, { withCredentials: true }),
      ]);
      setVehicles(v.data);
      setEmployees(e.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v.id);
    setForm({ ...emptyForm(), ...v, year: v.year || '', current_mileage: v.current_mileage ?? '', assigned_to: v.assigned_to || '' });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      year: form.year ? Number(form.year) : null,
      current_mileage: form.current_mileage !== '' && form.current_mileage !== null ? Number(form.current_mileage) : null,
    };
    try {
      if (editing) {
        await axios.put(`${API}/admin/vehicles/${editing}`, payload, { withCredentials: true });
        toast.success('Vehicle updated');
      } else {
        await axios.post(`${API}/admin/vehicles`, payload, { withCredentials: true });
        toast.success('Vehicle created');
      }
      setOpen(false); setEditing(null); refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${id}`, { withCredentials: true });
      toast.success('Deleted');
      refresh();
    } catch { toast.error('Failed'); }
  };

  const empName = (id) => employees.find(e => e.id === id)?.name || '—';

  const openDetail = async (v) => {
    setDetail(v);
    setMaintForm({ ...emptyMaint(), mileage: v.current_mileage ?? '' });
    try {
      const [m, c] = await Promise.all([
        axios.get(`${API}/admin/vehicles/${v.id}/maintenance`, { withCredentials: true }),
        axios.get(`${API}/admin/vehicles/${v.id}/checks`, { withCredentials: true }),
      ]);
      setMaint(m.data); setChecks(c.data);
    } catch { setMaint([]); setChecks([]); }
  };

  const addMaint = async (e) => {
    e.preventDefault();
    if (!detail) return;
    setSavingMaint(true);
    try {
      const payload = {
        date: maintForm.date,
        mileage: maintForm.mileage !== '' ? Number(maintForm.mileage) : null,
        type: maintForm.type,
        notes: maintForm.notes,
        cost: maintForm.cost !== '' ? Number(maintForm.cost) : null,
      };
      await axios.post(`${API}/admin/vehicles/${detail.id}/maintenance`, payload, { withCredentials: true });
      toast.success('Maintenance entry added');
      // refresh detail + list (current mileage / oil-due may have changed)
      const [m, updated] = await Promise.all([
        axios.get(`${API}/admin/vehicles/${detail.id}/maintenance`, { withCredentials: true }),
        axios.get(`${API}/admin/vehicles/${detail.id}`, { withCredentials: true }),
      ]);
      setMaint(m.data);
      setDetail(updated.data);
      setMaintForm({ ...emptyMaint(), mileage: updated.data.current_mileage ?? '' });
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add entry'); }
    setSavingMaint(false);
  };

  const removeMaint = async (mid) => {
    if (!detail || !window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${detail.id}/maintenance/${mid}`, { withCredentials: true });
      setMaint((prev) => prev.filter((x) => x.id !== mid));
      toast.success('Deleted');
    } catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="admin-vehicles-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2"><Truck className="w-6 h-6 text-[#D4AF37]" /> Vehicles & Trucks</h2>
          <p className="text-[#A3A3A3]">Fleet, assignments, mileage, weekly checks + maintenance</p>
        </div>
        <Button data-testid="add-vehicle-btn" onClick={openNew} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB]"><Plus className="w-4 h-4 mr-1" /> Add Vehicle</Button>
      </div>

      <div className="grid gap-2">
        {vehicles.length === 0 ? (
          <div className="bg-[#141414] border border-[#262626] rounded p-8 text-center text-[#A3A3A3]">No vehicles. Add your first truck.</div>
        ) : vehicles.map((v) => (
          <div key={v.id} data-testid={`vehicle-row-${v.id}`} className="bg-[#141414] border border-[#262626] rounded p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <p className="text-white font-medium flex items-center gap-2 flex-wrap">
                {v.name} <span className="text-[#525252] text-xs">{v.year} {v.make} {v.model}</span>
                {v.oil_change_due && (
                  <span data-testid={`vehicle-oil-due-${v.id}`} className="text-[10px] font-bold uppercase tracking-wider text-red-300 bg-red-500/15 border border-red-500/50 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Oil Change Due
                  </span>
                )}
              </p>
              <p className="text-[#A3A3A3] text-xs">Plate {v.license_plate || '—'} · VIN {v.vin || '—'}</p>
            </div>
            <div className="text-xs space-y-0.5">
              <p className="text-[#A3A3A3]">Dept: <span className="text-[#D4AF37] uppercase">{(v.department || '—').replace(/_/g, ' ')}</span></p>
              <p className="text-[#A3A3A3]">Assigned to: <span className="text-white">{v.assigned_to ? empName(v.assigned_to) : 'Unassigned'}</span></p>
            </div>
            <div className="text-xs space-y-0.5">
              <p className="text-[#A3A3A3] flex items-center gap-1"><Gauge className="w-3 h-3" /> Current: <span className="text-white" data-testid={`vehicle-mileage-${v.id}`}>{fmtMi(v.current_mileage)}</span></p>
              <p className="text-[#A3A3A3]">Next oil: <span className="text-white">{v.next_oil_change_mileage ? fmtMi(v.next_oil_change_mileage) : '—'}</span></p>
            </div>
            <div className="flex gap-1">
              <Button data-testid={`vehicle-detail-${v.id}`} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10" onClick={() => openDetail(v)}><Wrench className="w-3 h-3 mr-1" /> Maintenance</Button>
              <Button data-testid={`edit-vehicle-${v.id}`} size="sm" variant="outline" className="border-[#262626] text-white hover:bg-[#262626]" onClick={() => openEdit(v)}><Pencil className="w-3 h-3" /></Button>
              <Button data-testid={`delete-vehicle-${v.id}`} size="sm" variant="outline" className="border-red-700 text-red-400 hover:bg-red-900/30" onClick={() => remove(v.id)}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>

      {/* Add / Edit vehicle */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="bg-[#0D0D0D] border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader><SheetTitle className="text-[#D4AF37]">{editing ? 'Edit Vehicle' : 'New Vehicle'}</SheetTitle></SheetHeader>
          <form onSubmit={save} className="mt-5 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><Label className="text-white">Name / Label</Label><Input data-testid="v-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Truck 1 — Service Van" /></div>
              <div><Label className="text-white">Year</Label><Input data-testid="v-year" type="number" value={form.year} onChange={(e) => setForm({...form, year: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-white">License Plate</Label><Input data-testid="v-plate" value={form.license_plate} onChange={(e) => setForm({...form, license_plate: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-white">Make</Label><Input data-testid="v-make" value={form.make} onChange={(e) => setForm({...form, make: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-white">Model</Label><Input data-testid="v-model" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div className="col-span-2"><Label className="text-white">VIN</Label><Input data-testid="v-vin" value={form.vin} onChange={(e) => setForm({...form, vin: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div className="col-span-2">
                <Label className="text-white">Current Mileage {editing ? '' : '(beginning odometer)'}</Label>
                <Input data-testid="v-mileage" type="number" min="0" value={form.current_mileage} onChange={(e) => setForm({...form, current_mileage: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="e.g. 120000" />
              </div>
              <div>
                <Label className="text-white">Department</Label>
                <select data-testid="v-dept" value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded h-10 px-3 text-sm">
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-white">Assigned To</Label>
                <select data-testid="v-assign" value={form.assigned_to} onChange={(e) => setForm({...form, assigned_to: e.target.value})} className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded h-10 px-3 text-sm">
                  <option value="">Unassigned</option>
                  {employees.map(em => <option key={em.id} value={em.id}>{em.name}</option>)}
                </select>
              </div>
              <div><Label className="text-white">Insurance Expires</Label><Input data-testid="v-ins" type="date" value={form.insurance_expires || ''} onChange={(e) => setForm({...form, insurance_expires: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div><Label className="text-white">Next Oil Change (date)</Label><Input data-testid="v-next-oil" type="date" value={form.next_oil_change || ''} onChange={(e) => setForm({...form, next_oil_change: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
              <div className="col-span-2"><Label className="text-white">Notes</Label><Textarea data-testid="v-notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
            </div>
            <Button data-testid="v-submit" type="submit" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] w-full">{editing ? 'Save Changes' : 'Create Vehicle'}</Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Maintenance & Repairs + weekly check history */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <SheetContent className="bg-[#0D0D0D] border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto" data-testid="vehicle-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="text-[#D4AF37] flex items-center gap-2"><Wrench className="w-5 h-5" /> {detail.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-5">
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-[#141414] border border-[#262626] rounded p-3">
                    <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Current Mileage</p>
                    <p className="text-white text-lg font-bold" data-testid="detail-current-mileage">{fmtMi(detail.current_mileage)}</p>
                  </div>
                  <div className="bg-[#141414] border border-[#262626] rounded p-3">
                    <p className="text-[#A3A3A3] text-[10px] uppercase tracking-wider">Next Oil Change</p>
                    <p className="text-white text-lg font-bold" data-testid="detail-next-oil">{detail.next_oil_change_mileage ? fmtMi(detail.next_oil_change_mileage) : '—'}</p>
                  </div>
                </div>
                {detail.oil_change_due && (
                  <div data-testid="detail-oil-due" className="rounded-md bg-red-500/15 border border-red-500/50 px-3 py-2 text-red-200 text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Oil Change Due — current mileage has reached the next-due mark.
                  </div>
                )}

                {/* Add maintenance */}
                <div>
                  <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Wrench className="w-4 h-4 text-[#D4AF37]" /> Maintenance &amp; Repairs</h3>
                  <form onSubmit={addMaint} className="bg-[#141414] border border-[#262626] rounded p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-white text-xs">Date</Label><Input data-testid="maint-date" type="date" value={maintForm.date} onChange={(e) => setMaintForm({...maintForm, date: e.target.value})} required className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                      <div><Label className="text-white text-xs">Mileage</Label><Input data-testid="maint-mileage" type="number" min="0" value={maintForm.mileage} onChange={(e) => setMaintForm({...maintForm, mileage: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="Odometer" /></div>
                      <div>
                        <Label className="text-white text-xs">Type</Label>
                        <select data-testid="maint-type" value={maintForm.type} onChange={(e) => setMaintForm({...maintForm, type: e.target.value})} className="w-full bg-[#0A0A0A] border border-[#262626] text-white rounded h-10 px-2 text-sm">
                          {MAINT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div><Label className="text-white text-xs">Cost (optional)</Label><Input data-testid="maint-cost" type="number" min="0" step="0.01" value={maintForm.cost} onChange={(e) => setMaintForm({...maintForm, cost: e.target.value})} className="bg-[#0A0A0A] border-[#262626] text-white" placeholder="$" /></div>
                    </div>
                    <div><Label className="text-white text-xs">Notes</Label><Textarea data-testid="maint-notes" value={maintForm.notes} onChange={(e) => setMaintForm({...maintForm, notes: e.target.value})} rows={2} className="bg-[#0A0A0A] border-[#262626] text-white" /></div>
                    {maintForm.type === 'Oil Change' && (
                      <p className="text-[10px] text-[#A3A3A3]">Oil Change saves the mileage and auto-sets the next due at +{OIL_INTERVAL_LABEL} mi.</p>
                    )}
                    <Button data-testid="maint-submit" type="submit" disabled={savingMaint} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] w-full">{savingMaint ? 'Adding…' : 'Add Entry'}</Button>
                  </form>
                  <div className="mt-2 space-y-1.5">
                    {maint.length === 0 ? (
                      <p className="text-[#525252] text-xs italic px-1">No maintenance entries yet.</p>
                    ) : maint.map((m) => (
                      <div key={m.id} data-testid={`maint-row-${m.id}`} className="bg-[#141414] border border-[#262626] rounded px-3 py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-white text-sm">{m.type} <span className="text-[#525252] text-xs">· {m.date}{m.mileage != null ? ` · ${fmtMi(m.mileage)}` : ''}{m.cost != null ? ` · $${Number(m.cost).toLocaleString()}` : ''}</span></p>
                          {m.notes && <p className="text-[#A3A3A3] text-xs truncate">{m.notes}</p>}
                        </div>
                        <button type="button" data-testid={`maint-delete-${m.id}`} onClick={() => removeMaint(m.id)} className="text-[#525252] hover:text-red-400 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Weekly check history */}
                <div>
                  <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><ClipboardCheck className="w-4 h-4 text-[#D4AF37]" /> Weekly Vehicle Checks</h3>
                  <div className="space-y-1.5">
                    {checks.length === 0 ? (
                      <p className="text-[#525252] text-xs italic px-1">No weekly checks recorded yet.</p>
                    ) : checks.map((c) => {
                      const issues = CHECK_ITEMS.filter(([k]) => !c[k]).map(([, label]) => label);
                      return (
                        <div key={c.id} data-testid={`check-row-${c.id}`} className="bg-[#141414] border border-[#262626] rounded px-3 py-2">
                          <p className="text-white text-sm">Week of {c.week_start} <span className="text-[#525252] text-xs">· {fmtMi(c.mileage)} · {c.submitted_by_name || 'Tech'}</span></p>
                          {issues.length > 0
                            ? <p className="text-amber-300 text-xs">Not OK: {issues.join(', ')}</p>
                            : <p className="text-emerald-400 text-xs">All items checked OK</p>}
                          {c.notes && <p className="text-[#A3A3A3] text-xs">“{c.notes}”</p>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

const OIL_INTERVAL_LABEL = (3000).toLocaleString();

export default Vehicles;
